import React from 'react';
import { Drawer, Button, Switch } from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../../redux/store';
import {
  setShowCreateForm,
  setCustomizationData,
  type CustomizationData,
} from '../../../redux/Customizations';
import {
  FormItemLabel,
  HelperText,
  StyledInput,
  StyledNumberInput,
  FormActions,
  FullWidthSpaceLarge,
  FlexItem,
  FullWidthSpaceVertical,
  CreateButtonPrimary,
} from './Styles';

interface CreateCustomizationDrawerProps {
  open: boolean;
  onClose: () => void;
  onSubmit: () => Promise<void>;
  submitting: boolean;
}

const CreateCustomizationDrawer: React.FC<CreateCustomizationDrawerProps> = ({
  open,
  onClose,
  onSubmit,
  submitting,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const { customizationData } = useSelector((state: RootState) => state.customizations);

  const handleCancel = () => {
    dispatch(setShowCreateForm(false));
    dispatch(setCustomizationData(null));
    onClose();
  };

  const handleInputChange = (field: keyof CustomizationData, value: any) => {
    if (customizationData) {
      dispatch(
        setCustomizationData({
          ...customizationData,
          [field]: value,
        })
      );
    }
  };

  if (!customizationData) {
    return null;
  }

  return (
    <Drawer
      title="Create New Customization"
      placement="right"
      width={500}
      onClose={handleCancel}
      open={open}
      destroyOnClose
    >
      <FullWidthSpaceLarge direction="vertical" size="large">
        <div>
          <FormItemLabel>Customization Name *</FormItemLabel>
          <StyledInput
            placeholder="Enter customization name"
            value={customizationData.customization || ''}
            onChange={(e) => handleInputChange('customization', e.target.value)}
          />
        </div>

        <div>
          <FormItemLabel>Note (Optional)</FormItemLabel>
          <StyledInput
            placeholder="Add a description"
            value={customizationData.note || ''}
            onChange={(e) => handleInputChange('note', e.target.value)}
          />
        </div>

        <div>
          <FormItemLabel>Sequence Number *</FormItemLabel>
          <HelperText>Auto-calculated based on existing customizations</HelperText>
          <StyledNumberInput
            type="number"
            placeholder="Auto-calculated"
            value={customizationData.sequence_number || ''}
            onChange={(e) =>
              handleInputChange(
                'sequence_number',
                e.target.value === '' ? undefined : parseInt(e.target.value) || 1
              )
            }
          />
        </div>

        <FullWidthSpaceVertical size="middle">
          <FlexItem>
            <FormItemLabel>Max Quantity</FormItemLabel>
            <StyledNumberInput
              type="number"
              placeholder="0"
              value={customizationData.max_quantity || ''}
              onChange={(e) =>
                handleInputChange(
                  'max_quantity',
                  e.target.value === '' ? undefined : parseInt(e.target.value) || 0
                )
              }
            />
          </FlexItem>

          <FlexItem>
            <FormItemLabel>Min Quantity</FormItemLabel>
            <StyledNumberInput
              type="number"
              placeholder="0"
              value={customizationData.min_quantity || ''}
              onChange={(e) =>
                handleInputChange(
                  'min_quantity',
                  e.target.value === '' ? undefined : parseInt(e.target.value) || 0
                )
              }
            />
          </FlexItem>
        </FullWidthSpaceVertical>

        <FullWidthSpaceVertical size="middle">
          <FlexItem>
            <FormItemLabel>Multiple Selections</FormItemLabel>
            <Switch
              checked={customizationData.multiple_selections || false}
              onChange={(checked) => handleInputChange('multiple_selections', checked)}
            />
          </FlexItem>

          <FlexItem>
            <FormItemLabel>Sold Out</FormItemLabel>
            <Switch
              checked={customizationData.sold_out || false}
              onChange={(checked) => handleInputChange('sold_out', checked)}
            />
          </FlexItem>
        </FullWidthSpaceVertical>

        <div>
          <FormItemLabel>Multiple Selections Quantity</FormItemLabel>
          <StyledNumberInput
            type="number"
            placeholder="0"
            value={customizationData.multiple_selections_quantity || ''}
            onChange={(e) =>
              handleInputChange(
                'multiple_selections_quantity',
                e.target.value === '' ? undefined : parseInt(e.target.value) || 0
              )
            }
          />
        </div>

        <FormActions>
          <Button onClick={handleCancel}>Cancel</Button>
          <CreateButtonPrimary onClick={onSubmit} loading={submitting}>
            Create Customization
          </CreateButtonPrimary>
        </FormActions>
      </FullWidthSpaceLarge>
    </Drawer>
  );
};

export default CreateCustomizationDrawer;

